import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { PaginatedCollectionsResponse } from '@/api/services/collections-service';
import { ORGANIZER_COLLECTIONS_PAGE_SIZE } from '@/constants/organizer-collections';
import type { Collection } from '@/types/collection';
import { withBasePath } from '@/utils/withBasePath';

async function fetchCollectionsPage(
  organizerId: string,
  page: number,
  pageSize: number,
): Promise<PaginatedCollectionsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await fetch(
    withBasePath(`/api/organizers/${encodeURIComponent(organizerId)}/collections/?${params}`),
    {
      credentials: 'include',
    },
  );
  if (!res.ok) {
    throw new Error(`Collections request failed: ${res.status}`);
  }
  return (await res.json()) as PaginatedCollectionsResponse;
}

export function useCollections(
  organizerId: string,
  initialCollections: Collection[],
  initialHasMore: boolean,
  initialTotal: number,
) {
  const pageSize = useMemo(() => ORGANIZER_COLLECTIONS_PAGE_SIZE, []);

  const firstPage: PaginatedCollectionsResponse = {
    collections: initialCollections,
    hasMore: initialHasMore,
    total: initialTotal,
  };

  const { data, isLoading, isError, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['organizer-collections', organizerId, pageSize],
    queryFn: ({ pageParam }) =>
      fetchCollectionsPage(organizerId, typeof pageParam === 'number' ? pageParam : 1, pageSize),
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length + 1 : undefined),
    initialData: {
      pages: [firstPage],
      pageParams: [1],
    },
    staleTime: Infinity,
  });

  const collections = data?.pages.flatMap((p) => p.collections) ?? [];

  const lastPage = data?.pages?.[data.pages.length - 1];
  const hasMorePages = Boolean(lastPage?.hasMore);

  return {
    collections,
    isLoading,
    isError,
    fetchNextPage,
    hasMorePages,
    isFetchingNextPage,
  };
}
